import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { User, CheckCircle2, XCircle } from "lucide-react";
import type { DocumentUser } from "@shared/schema";

interface UserIdentityConfirmationDialogProps {
  isOpen: boolean;
  user: DocumentUser | null;
  onConfirm: () => void;
  onDecline: () => void;
  documentColor: string;
}

export function UserIdentityConfirmationDialog({
  isOpen,
  user,
  onConfirm,
  onDecline,
  documentColor,
}: UserIdentityConfirmationDialogProps) {
  if (!user) return null;

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent 
        className="w-full max-w-[min(100vw-1.5rem,24rem)] sm:max-w-sm p-6 rounded-lg overflow-hidden [&>button]:hidden" 
        onEscapeKeyDown={(e) => e.preventDefault()} 
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex justify-center mb-3">
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center"
              style={{ backgroundColor: `${documentColor}20` }}
            >
              <User className="w-8 h-8" style={{ color: documentColor }} />
            </div>
          </div>
          <DialogTitle className="text-xl sm:text-2xl font-semibold text-center">
            Are you {user.name}?
          </DialogTitle>
          <DialogDescription className="text-sm sm:text-base text-center text-muted-foreground pt-1">
            {user.email}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 mt-6">
          <Button
            onClick={onConfirm}
            className="w-full min-h-[48px] sm:min-h-[52px] text-base sm:text-lg font-medium text-white border-0 hover:opacity-90 flex items-center justify-center gap-2"
            style={{ backgroundColor: documentColor }}
            data-testid="button-confirm-identity"
          >
            <CheckCircle2 className="w-5 h-5" />
            Yes, that's me
          </Button>
          
          <Button
            onClick={onDecline}
            variant="outline"
            className="w-full min-h-[48px] sm:min-h-[52px] text-base sm:text-lg font-medium flex items-center justify-center gap-2"
            data-testid="button-decline-identity"
          >
            <XCircle className="w-5 h-5" />
            No, pick someone else
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
